import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { getUserColor } from "encamp-shared/src/utils/colors";
import { gql } from "generated-graphql";
import { PaymentComment } from "generated-graphql/graphql";
import { useState } from "react";
import { prettyPrintDateTime } from "util/dates";

const ADD_PAYMENT_COMMENT = gql(`
  mutation AddPaymentComment($input: PaymentCommentInput!) {
    addPaymentComment(input: $input) {
      id
      message
      createdBy
      createdByEmail
      createdAt
      deleted
    }
  }
`);

const DELETE_PAYMENT_COMMENT = gql(`
  mutation DeletePaymentComment($id: ID!) {
    deletePaymentComment(id: $id) {
      id
      deleted
      deletedAt
      deletedBy
    }
  }
`);

interface PaymentCommentStreamProps {
  paymentId: string;
  comments: PaymentComment[];
  currentUserId?: string;
  onCommentAdded?: (newComment: PaymentComment) => void;
  onCommentDeleted?: (deletedCommentId: string) => void;
}

export function PaymentCommentStream({
  paymentId,
  comments,
  currentUserId,
  onCommentAdded,
  onCommentDeleted,
}: PaymentCommentStreamProps) {
  const alerts = useAlerts();
  const [newComment, setNewComment] = useState("");

  const [addComment, { loading: isAddingComment }] = useMutation(ADD_PAYMENT_COMMENT, {
    onCompleted: (data) => {
      setNewComment("");
      // Pass the new comment to parent for cache update instead of refetching
      onCommentAdded?.(data.addPaymentComment);
      alerts.success("Comment added successfully");
    },
    onError: (error) => {
      alerts.error("Failed to add comment", error);
    },
    update: (cache, { data }) => {
      if (data?.addPaymentComment) {
        // Update the payment cache to include the new comment
        cache.modify({
          id: cache.identify({ __typename: "Payment", id: paymentId }),
          fields: {
            comments(existingComments = []) {
              const newCommentRef = cache.writeFragment({
                data: data.addPaymentComment,
                fragment: gql(`
                    fragment NewComment on PaymentComment {
                      id
                      message
                      createdBy
                      createdByEmail
                      createdAt
                      deleted
                    }
                  `),
                fragmentName: "NewComment",
              });
              return [...existingComments, newCommentRef];
            },
          },
        });
      }
    },
  });

  const [deleteComment, { loading: isDeletingComment }] = useMutation(DELETE_PAYMENT_COMMENT, {
    onCompleted: (data) => {
      // Pass the deleted comment ID to parent for cache update instead of refetching
      onCommentDeleted?.(data.deletePaymentComment.id);
      alerts.success("Comment deleted successfully");
    },
    onError: (error) => {
      alerts.error("Failed to delete comment", error);
    },
    update: (cache, { data }) => {
      if (data?.deletePaymentComment) {
        // Update the payment cache to mark the comment as deleted
        cache.modify({
          id: cache.identify({ __typename: "Payment", id: paymentId }),
          fields: {
            comments(existingComments = []) {
              return existingComments.map((commentRef: any) => {
                const comment = cache.readFragment({
                  id: commentRef.__ref,
                  fragment: gql(`
                      fragment CommentToDelete on PaymentComment {
                        id
                        deleted
                      }
                    `),
                  fragmentName: "CommentToDelete",
                });

                if (comment && comment.id === data.deletePaymentComment.id) {
                  // Update the comment to mark it as deleted
                  cache.writeFragment({
                    id: commentRef.__ref,
                    fragment: gql(`
                        fragment DeletedComment on PaymentComment {
                          id
                          deleted
                          deletedAt
                          deletedBy
                        }
                      `),
                    fragmentName: "DeletedComment",
                    data: {
                      ...comment,
                      deleted: true,
                      deletedAt: data.deletePaymentComment.deletedAt,
                      deletedBy: data.deletePaymentComment.deletedBy,
                    },
                  });
                }

                return commentRef;
              });
            },
          },
        });
      }
    },
  });

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    await addComment({
      variables: {
        input: {
          paymentId,
          message: newComment.trim(),
        },
      },
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // NOTE: Enter submits comment, Shift+Enter adds carriage return (line break)
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleAddComment();
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (newComment.trim()) {
        // If there's content, clear it and keep focus
        setNewComment("");
        (event.target as HTMLTextAreaElement).focus();
      } else {
        // If no content, let the event bubble up to close the dialog
        // Remove focus from the textarea to allow dialog to handle the escape
        (event.target as HTMLTextAreaElement).blur();
      }
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    await deleteComment({
      variables: {
        id: commentId,
      },
    });
  };

  // Filter out deleted comments and sort by creation date
  const visibleComments = comments
    .filter((comment) => !comment.deleted)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Comments</Typography>

      {/* Display existing comments section */}
      {visibleComments.length > 0 ? (
        <Stack spacing={1}>
          {visibleComments.map((comment) => {
            const userColor = getUserColor(comment.createdByEmail);
            return (
              <Box
                key={comment.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  py: 0.5,
                  "&:hover .delete-button": {
                    opacity: 1,
                  },
                }}
              >
                {/* Quotation-style border */}
                <Box
                  sx={{
                    width: 3,
                    minHeight: 40,
                    backgroundColor: userColor,
                    borderRadius: "1.5px",
                    flexShrink: 0,
                    alignSelf: "stretch",
                  }}
                />

                {/* Delete button positioned just left of comment content */}
                {currentUserId && comment.createdBy === currentUserId && (
                  <IconButton
                    className="delete-button"
                    size="small"
                    onClick={() => handleDeleteComment(comment.id)}
                    disabled={isDeletingComment}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                      width: 16,
                      height: 16,
                      mt: 0.25,
                      "&:hover": {
                        opacity: 1,
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}

                {/* Comment content */}
                <Box
                  sx={{
                    flex: 1,
                    pl: currentUserId && comment.createdBy === currentUserId ? 0 : 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 0.5 }}>
                    {comment.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {comment.createdByEmail} • {prettyPrintDateTime(comment.createdAt)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Box
          sx={{
            p: 2,
            bgcolor: "grey.50",
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            No comments yet.
          </Typography>
        </Box>
      )}

      {/* Add new comment section */}
      <Box>
        <TextField
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          disabled={isAddingComment}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <Box sx={{ position: "absolute", bottom: 8, right: 8 }}>
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || isAddingComment}
                  variant="contained"
                  size="small"
                >
                  {isAddingComment ? "Adding..." : "Add Comment"}
                </Button>
              </Box>
            ),
          }}
          sx={{
            "& .MuiInputBase-root": {
              position: "relative",
              paddingBottom: "48px", // Make room for the button
            },
          }}
        />
      </Box>
    </Stack>
  );
}
